import type { Entitlement } from '../types'
import { Box, ChevronLeft, ChevronRight } from './Icons'

type Props = {
  rows: Entitlement[]
  total: number
  limit: number
  offset: number
  busy: boolean
  selectedId: string | null
  onSelect: (id: string) => void
  onPageChange: (offset: number) => void
}

function titleCase(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

function paginationPages(current: number, total: number) {
  const first = Math.max(1, Math.min(current - 2, total - 4))
  const last = Math.min(total, first + 4)
  return Array.from({ length: Math.max(0, last - first + 1) }, (_, index) => first + index)
}

export function EntitlementsTable({
  rows,
  total,
  limit,
  offset,
  busy,
  selectedId,
  onSelect,
  onPageChange,
}: Props) {
  const currentPage = Math.floor(offset / limit) + 1
  const totalPages = Math.max(1, Math.ceil(total / limit))
  const start = total === 0 ? 0 : offset + 1
  const end = Math.min(offset + rows.length, total)

  return (
    <>
      <div className={`table-scroll ${busy ? 'is-loading' : ''}`} aria-busy={busy}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Product</th>
              <th>Plan</th>
              <th>Seats</th>
              <th>Updates until</th>
              <th>Channel</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                className={row.id === selectedId ? 'selected' : ''}
                onClick={() => onSelect(row.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    onSelect(row.id)
                  }
                }}
                tabIndex={0}
              >
                <td>
                  <strong>{row.customer_name}</strong>
                  <span>{row.email}</span>
                </td>
                <td>
                  <span className="product-cell">
                    <Box size={16} />
                    {row.product}
                  </span>
                </td>
                <td>{row.plan}</td>
                <td>
                  {row.seat_limit_enforced
                    ? `${row.active_seats} of ${row.seat_count}`
                    : `${row.active_seats} active · Unlimited (legacy)`}
                </td>
                <td>{row.updates_until ?? '—'}</td>
                <td>{titleCase(row.channel)}</td>
                <td>
                  <span className="status-text">
                    <i
                      className={`square ${row.status === 'active' ? 'success' : 'muted'}`}
                    />
                    {titleCase(row.status)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <div className="empty-state">No entitlements match these filters.</div>
        )}
      </div>
      <footer className="table-footer">
        <span>
          Showing {start} to {end} of {total.toLocaleString()} results
        </span>
        <div className="pagination" aria-label="Pagination">
          <button
            type="button"
            aria-label="Previous page"
            disabled={currentPage <= 1 || busy}
            onClick={() => onPageChange(Math.max(0, offset - limit))}
          >
            <ChevronLeft size={15} />
          </button>
          {paginationPages(currentPage, totalPages).map((page) => (
            <button
              className={page === currentPage ? 'current' : ''}
              type="button"
              key={page}
              disabled={busy}
              aria-current={page === currentPage ? 'page' : undefined}
              onClick={() => onPageChange((page - 1) * limit)}
            >
              {page}
            </button>
          ))}
          <button
            type="button"
            aria-label="Next page"
            disabled={currentPage >= totalPages || busy}
            onClick={() => onPageChange(offset + limit)}
          >
            <ChevronRight size={15} />
          </button>
        </div>
      </footer>
    </>
  )
}
